import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  MessageSquare,
  MoreHorizontal,
  Edit,
  Trash2,
  Reply,
  User,
  ChevronDown,
  ChevronRight,
  Minus,
  Plus,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useDeleteComment, useUpdateComment } from '../../hooks/useComments';
import { CommentForm } from './CommentForm';
import type { Comment } from '../../services/productService';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

interface CommentItemProps {
  comment: Comment;
  productId: number;
  showReplies?: boolean;
  level?: number;
  maxLevel?: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * CommentItem Component
 *
 * Displays individual comments with:
 * - Author information and timestamps
 * - Edit/delete functionality for own comments
 * - Reply functionality with threading
 * - Collapsible discussions
 * - Responsive design with proper indentation
 */
export function CommentItem({
  comment,
  productId,
  showReplies = true,
  level = 0,
  maxLevel = 10,
  isCollapsed = false,
  onToggleCollapse,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(true);

  // Get translations
  const { getCommentsText } = useProductDetailPageTranslations();

  const deleteCommentMutation = useDeleteComment(productId);
  const updateCommentMutation = useUpdateComment(productId, comment.id);

  // Calculate indentation based on nesting level
  const indentLevel = Math.min(level, maxLevel);
  const marginLeft = indentLevel * 24; // 24px per level

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteCommentMutation.mutate(comment.id);
    setShowDeleteDialog(false);
  };

  const handleReply = () => {
    setShowReplyForm(true);
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const handleReplySuccess = () => {
    setShowReplyForm(false);
  };

  const handleReplyCancel = () => {
    setShowReplyForm(false);
  };

  const toggleReplies = () => {
    setRepliesExpanded(!repliesExpanded);
  };

  // Get author initials for avatar
  const getAuthorInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return getCommentsText('item.timestamps.unknownTime');
    }
  };

  const hasReplies = comment.replies && comment.replies.length > 0;
  const canShowReplies = showReplies && hasReplies && level < maxLevel;
  const isTopLevel = level === 0;

  // Get thread depth explanation
  const getThreadDepthExplanation = (depth: number) => {
    if (depth === 0) return getCommentsText('item.threadDepthExplanations.0');
    if (depth === 1) return getCommentsText('item.threadDepthExplanations.1');
    if (depth === 2) return getCommentsText('item.threadDepthExplanations.2');
    if (depth === 3) return getCommentsText('item.threadDepthExplanations.3');
    return getCommentsText('item.threadDepthExplanations.default', { depth });
  };

  return (
    <div className="space-y-3" style={{ marginLeft: `${marginLeft}px` }}>
      <Card
        className={`
        ${level > 0 ? 'border-l-2 border-l-muted' : ''} 
        ${isCollapsed ? 'opacity-50' : ''} 
        ${isTopLevel ? 'bg-slate-100/70 dark:bg-slate-900/40 border-slate-300 dark:border-slate-700' : ''}
      `.trim()}
      >
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Comment Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                {/* Collapse button for top-level comments with replies */}
                {isTopLevel && hasReplies && onToggleCollapse && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleCollapse}
                    className="size-6 p-0 hover:bg-muted"
                    title={
                      isCollapsed
                        ? getCommentsText('item.actions.expandDiscussion')
                        : getCommentsText('item.actions.collapseDiscussion')
                    }
                  >
                    {isCollapsed ? <Plus className="size-3" /> : <Minus className="size-3" />}
                  </Button>
                )}

                <Avatar className="size-8">
                  <AvatarFallback className="text-xs">
                    {getAuthorInitials(comment.author_full_name)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">{comment.author_full_name}</span>
                    {comment.is_reply && (
                      <Badge variant="outline" className="text-xs">
                        {getCommentsText('item.badges.reply')}
                      </Badge>
                    )}
                    {comment.thread_depth > 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="cursor-help text-xs">
                              {getCommentsText('item.badges.level', {
                                depth: comment.thread_depth,
                              })}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{getThreadDepthExplanation(comment.thread_depth)}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {getCommentsText('item.tooltips.threadDepth')}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(comment.created_at)}
                    {comment.updated_at !== comment.created_at && (
                      <span className="ml-1">{getCommentsText('item.badges.edited')}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="size-8 p-0">
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleReply}>
                    <Reply className="mr-2 size-4" />
                    {getCommentsText('item.actions.reply')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleEdit}>
                    <Edit className="mr-2 size-4" />
                    {getCommentsText('item.actions.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 size-4" />
                    {getCommentsText('item.actions.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Comment Body - Only show if not collapsed */}
            {!isCollapsed && (
              <>
                {isEditing ? (
                  <CommentForm
                    productId={productId}
                    initialValue={comment.body}
                    commentId={comment.id}
                    onSuccess={handleEditSuccess}
                    onCancel={handleEditCancel}
                    mode="edit"
                  />
                ) : (
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{comment.body}</p>
                  </div>
                )}

                {/* Comment Actions */}
                {!isEditing && (
                  <div className="flex items-center space-x-4 pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReply}
                      className="h-8 px-2 text-xs"
                    >
                      <Reply className="mr-1 size-3" />
                      {getCommentsText('item.actions.reply')}
                    </Button>

                    {comment.replies_count > 0 && canShowReplies && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleReplies}
                        className="h-8 px-2 text-xs"
                      >
                        {repliesExpanded ? (
                          <ChevronDown className="mr-1 size-3" />
                        ) : (
                          <ChevronRight className="mr-1 size-3" />
                        )}
                        {comment.replies_count === 1
                          ? getCommentsText('item.replies.single', {
                              count: comment.replies_count,
                            })
                          : getCommentsText('item.replies.multiple', {
                              count: comment.replies_count,
                            })}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Collapsed state indicator */}
            {isCollapsed && hasReplies && (
              <div className="text-xs text-muted-foreground">
                {getCommentsText('item.collapsed.discussionCollapsed', {
                  count: comment.replies_count + 1,
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reply Form - Only show if not collapsed */}
      {!isCollapsed && showReplyForm && (
        <Card className="ml-6">
          <CardContent className="p-4">
            <CommentForm
              productId={productId}
              parentId={comment.id}
              onSuccess={handleReplySuccess}
              onCancel={handleReplyCancel}
              placeholder={getCommentsText('form.placeholders.replyTo', {
                name: comment.author_full_name,
              })}
            />
          </CardContent>
        </Card>
      )}

      {/* Replies - Only show if not collapsed */}
      {!isCollapsed && canShowReplies && repliesExpanded && (
        <div className="space-y-3">
          {comment.replies?.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              productId={productId}
              showReplies={showReplies}
              level={level + 1}
              maxLevel={maxLevel}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getCommentsText('item.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {getCommentsText('item.deleteDialog.description')}
              {comment.replies_count > 0 && (
                <span className="mt-2 block text-amber-600 dark:text-amber-400">
                  {getCommentsText('item.deleteDialog.withRepliesWarning', {
                    count: comment.replies_count,
                  })}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {getCommentsText('item.deleteDialog.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCommentMutation.isPending}
            >
              {deleteCommentMutation.isPending
                ? getCommentsText('item.deleteDialog.actions.deleting')
                : getCommentsText('item.deleteDialog.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
